import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface Role {
  id: number;
  name: string;
  slug: string;
  description: string | null;
}

export default function RolesTab() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get<Role[]>("/users/roles");
      setRoles(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-100 text-primary-700">
              <i className="ri-shield-user-line"></i>
            </span>
            <div>
              <h3 className="font-heading text-base text-foreground-950">Custom Roles</h3>
              <p className="text-xs text-foreground-600">Roles available in the system and their access levels.</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {error && (
            <div className="rounded-xl bg-accent-100 border border-accent-300 p-3 text-sm text-accent-900 mb-4">
              {error}
            </div>
          )}
          {loading ? (
            <p className="text-center text-sm text-foreground-600 py-8">
              <i className="ri-loader-4-line animate-spin"></i> Loading...
            </p>
          ) : (
            <div className="space-y-3">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center gap-4 rounded-xl bg-background-100 border border-background-200 p-4"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 text-primary-700 text-sm font-bold uppercase">
                    {role.name.slice(0, 2)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-950">{role.name}</p>
                    <p className="text-xs text-foreground-600">{role.description || role.slug}</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-background-200 text-foreground-700 text-[11px] font-semibold uppercase">
                    {role.slug}
                  </span>
                </div>
              ))}
              {roles.length === 0 && (
                <p className="text-center text-sm text-foreground-600 py-8">No roles found.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}